// Sistema → Warehouses (ver informe "LYNX — Multi-Warehouse, Dropoffs,
// Pickers y Estanterías", PR 2). Administra la entidad Warehouse en sí
// (Crossdocks y Dropoffs); las estanterías de cada uno se administran
// reutilizando ShelvesPage tal cual (sin duplicar su lógica), pasándole
// el id del Warehouse elegido en vez de depender del depósito único.
import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { useLeaflet } from "../hooks/useLeaflet";
import { buildGeocodeQuery, GeocodeCandidate } from "../components/warehouse/geoQuery";
import ShelvesPage from "./ShelvesPage";

type WarehouseType = "CROSSDOCK" | "DROPOFF";
type WarehouseRow = {
  id: number; name: string; type: WarehouseType; address: string | null;
  postal_code: string | null; latitude: number | null; longitude: number | null;
  active: boolean; shelves_count: number; dropoff_tariff_id: number | null;
};
type Picker = { user_id: number; username: string; first_name: string | null; last_name: string | null; active: boolean };
type TariffCatalogEntry = { id: number; name: string; amount: string };

const TYPE_LABEL: Record<WarehouseType, string> = { CROSSDOCK: "Crossdock", DROPOFF: "Dropoff" };

const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });
const selectStyle: React.CSSProperties = { minHeight: "40px", padding: "0 10px", borderRadius: "12px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)", font: "inherit", width: "100%" };

function Modal({ title, onClose, children, width = "560px" }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; width?: string }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", zIndex: 100, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: width }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>{title}</h3>
            <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

export default function WarehousesPage() {
  const { token, financeAccess } = useAuth();
  const req = useCallback(async (path: string, init: { method?: string; body?: unknown } = {}) => {
    const response = await authFetch(path, { token, method: init.method ?? "GET", json: init.body !== undefined, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json?.ok === false) {
      const err: any = new Error(json?.error || `Error ${response.status}`);
      if (json?.blockers) err.blockers = json.blockers;
      throw err;
    }
    return json;
  }, [token]);

  const [warehouses, setWarehouses] = useState<WarehouseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<WarehouseRow | "new" | null>(null);
  const [detail, setDetail] = useState<WarehouseRow | null>(null);
  const [geoEditing, setGeoEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setWarehouses((await req("/v2/warehouses")).warehouses ?? []); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }, [req]);
  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 6000); };

  const toggleActive = async (w: WarehouseRow) => {
    try {
      await req(`/v2/warehouses/${w.id}`, { method: "PUT", body: { active: !w.active } });
      flash(`${w.name} ${w.active ? "desactivado" : "activado"}`);
      load();
    } catch (e: any) {
      setError(Array.isArray(e.blockers) && e.blockers.length ? `${e.message}: ${e.blockers.join("; ")}` : e.message);
    }
  };

  // Eliminación definitiva: el backend ya protege el Depósito principal
  // (id=1) y bloquea si hay actividad/dependencias reales -- acá solo se
  // confirma la intención (es irreversible, a diferencia de Desactivar) y
  // se muestra el mismo patrón de blockers que toggleActive.
  const deleteWarehouse = async (w: WarehouseRow) => {
    if (!window.confirm(`¿Eliminar «${w.name}» definitivamente? Esta acción no se puede deshacer.`)) return;
    try {
      await req(`/v2/warehouses/${w.id}`, { method: "DELETE" });
      flash(`${w.name} eliminado`);
      load();
    } catch (e: any) {
      setError(Array.isArray(e.blockers) && e.blockers.length ? `${e.message}: ${e.blockers.join("; ")}` : e.message);
    }
  };

  if (detail) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
            <div>
              <Button variant="ghost" onClick={() => setDetail(null)}>← Volver a Warehouses</Button>
              <h2 style={{ ...titleStyle, marginTop: "10px" }}>{detail.name}</h2>
              <p style={subtitleStyle}>
                {TYPE_LABEL[detail.type]} {detail.address ? `· ${detail.address}` : ""} {detail.postal_code ? `· CP ${detail.postal_code}` : ""}
                {" · "}{detail.active ? <span style={badge("var(--success)")}>Activo</span> : <span style={badge("var(--warning, #d29922)")}>Inactivo</span>}
              </p>
              <p style={{ ...smallMutedStyle, marginTop: "6px" }}>
                {detail.latitude != null && detail.longitude != null
                  ? `📍 ${detail.latitude.toFixed(5)}, ${detail.longitude.toFixed(5)}`
                  : "⚠ Sin coordenadas: las paradas de colecta que lo usen como origen no van a tener ubicación en el mapa."}
              </p>
            </div>
            <Button variant="secondary" onClick={() => setGeoEditing(true)}>Geolocalizar</Button>
          </div>
        </Card>
        <PickersCard warehouseId={detail.id} req={req} />
        <ShelvesPage warehouseId={detail.id} warehouseName={detail.name} />
        {geoEditing ? (
          <WarehouseGeoLocationModal
            warehouseId={detail.id} address={detail.address} postalCode={detail.postal_code} req={req}
            onClose={() => setGeoEditing(false)}
            onSaved={(w) => { setGeoEditing(false); setDetail(w); flash(`Coordenadas de «${w.name}» guardadas`); load(); }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Warehouses</h2>
            <p style={subtitleStyle}>
              Crossdocks (depósito operativo de clasificación y despacho) y Dropoffs (receptoría de paquetes para que un Driver los retire).
              Cada uno administra sus propias estanterías por separado.
            </p>
          </div>
          <Button onClick={() => setEditing("new")}>Nuevo Warehouse</Button>
        </div>
      </Card>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error} <Button variant="ghost" onClick={() => setError(null)}>Cerrar</Button></div> : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      <Card>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Nombre</th><th style={thStyle}>Tipo</th><th style={thStyle}>Dirección</th><th style={thStyle}>Estanterías</th><th style={thStyle}>Estado</th><th style={thStyle}></th></tr></thead>
            <tbody>
              {loading && !warehouses.length ? <tr><td style={tdStyle} colSpan={6}>Cargando…</td></tr>
                : !warehouses.length ? <tr><td style={tdStyle} colSpan={6}>Todavía no hay Warehouses. Creá el primero con «Nuevo Warehouse».</td></tr>
                : warehouses.map((w) => (
                  <tr key={w.id}>
                    <td style={tdStyle}><b>{w.name}</b></td>
                    <td style={tdStyle}>{TYPE_LABEL[w.type]}</td>
                    <td style={tdStyle}>
                      {w.address ?? <span style={smallMutedStyle}>—</span>}{w.postal_code ? <div style={smallMutedStyle}>CP {w.postal_code}</div> : null}
                      <div style={smallMutedStyle}>{w.latitude != null && w.longitude != null ? "📍 geolocalizado" : "⚠ sin coordenadas"}</div>
                    </td>
                    <td style={tdStyle}>{w.shelves_count}</td>
                    <td style={tdStyle}>{w.active ? <span style={badge("var(--success)")}>Activo</span> : <span style={badge("var(--warning, #d29922)")}>Inactivo</span>}</td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <Button variant="secondary" onClick={() => setDetail(w)}>Ver detalle</Button>
                        <Button variant="ghost" onClick={() => setEditing(w)}>Editar</Button>
                        <Button variant="ghost" onClick={() => toggleActive(w)}>{w.active ? "Desactivar" : "Activar"}</Button>
                        {w.id !== 1 ? <Button variant="ghost" onClick={() => deleteWarehouse(w)}>Eliminar</Button> : null}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editing !== null ? (
        <WarehouseModal
          warehouse={editing === "new" ? null : editing}
          req={req}
          financeAccess={financeAccess}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); flash(msg); load(); }}
        />
      ) : null}
    </div>
  );
}

function PickersCard({ warehouseId, req }: { warehouseId: number; req: any }) {
  const [pickers, setPickers] = useState<Picker[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try { setPickers((await req(`/v2/warehouses/${warehouseId}/pickers`)).pickers ?? []); } catch (e: any) { setError(e.message); }
    })();
  }, [req, warehouseId]);

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px 0", fontSize: "16px", color: "var(--text-primary)" }}>Pickers asignados</h3>
      <p style={{ ...smallMutedStyle, margin: "0 0 10px 0" }}>
        Se asignan desde Usuarios → crear/editar un ADMIN con perfil Picker. No hay selector para el operario: la asignación la hace un administrador.
      </p>
      {error ? <div style={{ color: "var(--danger)" }}>{error}</div> : !pickers ? (
        <span style={smallMutedStyle}>Cargando…</span>
      ) : !pickers.length ? (
        <span style={smallMutedStyle}>Todavía no hay ningún Picker asignado a este Warehouse.</span>
      ) : (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {pickers.map((p) => (
            <span key={p.user_id} style={badge(p.active ? "var(--success)" : "var(--text-secondary)")}>
              {p.first_name ?? p.username} {p.last_name ?? ""} {!p.active ? "(inactivo)" : ""}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function WarehouseModal({ warehouse, req, financeAccess, onClose, onSaved }: { warehouse: WarehouseRow | null; req: any; financeAccess: boolean; onClose: () => void; onSaved: (msg: string) => void }) {
  const [name, setName] = useState(warehouse?.name ?? "");
  const [type, setType] = useState<WarehouseType>(warehouse?.type ?? "CROSSDOCK");
  const [address, setAddress] = useState(warehouse?.address ?? "");
  const [postalCode, setPostalCode] = useState(warehouse?.postal_code ?? "");
  const [tariffId, setTariffId] = useState(warehouse?.dropoff_tariff_id != null ? String(warehouse.dropoff_tariff_id) : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Catálogo de tarifas (ver informe "LYNX — Multi-Warehouse, Dropoffs,
  // Pickers y Estanterías", ajuste post-PR6: "Catálogo de tarifas"): solo
  // se pide si hace falta -- un Crossdock no tiene selector, y sin
  // finance_access el backend igual rechazaría /v2/dropoff-tariff/catalog
  // (403), así que ni se intenta.
  const [catalog, setCatalog] = useState<TariffCatalogEntry[] | null>(null);
  useEffect(() => {
    if (type !== "DROPOFF" || !financeAccess) { setCatalog(null); return; }
    let cancelled = false;
    req("/v2/dropoff-tariff/catalog").then((j: any) => { if (!cancelled) setCatalog(j.catalog ?? []); }).catch(() => { if (!cancelled) setCatalog([]); });
    return () => { cancelled = true; };
  }, [type, financeAccess, req]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body: Record<string, unknown> = { name, type, address: address.trim() || null, postal_code: postalCode.trim() || null };
      if (type === "DROPOFF" && financeAccess) {
        body.dropoff_tariff_id = tariffId ? Number(tariffId) : null;
      }
      const r = warehouse
        ? await req(`/v2/warehouses/${warehouse.id}`, { method: "PUT", body })
        : await req("/v2/warehouses", { method: "POST", body });
      onSaved(`Warehouse «${r.warehouse.name}» guardado`);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal title={warehouse ? `Editar «${warehouse.name}»` : "Nuevo Warehouse"} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <Input label="Nombre" value={name} onChange={(e: any) => setName(e.target.value)} placeholder="Dropoff Araoz" />
        <label style={{ display: "block" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "6px" }}>Tipo</div>
          <select value={type} onChange={(e) => setType(e.target.value as WarehouseType)} style={selectStyle}>
            <option value="CROSSDOCK">Crossdock — depósito operativo (clasificación y despacho)</option>
            <option value="DROPOFF">Dropoff — receptoría (retiro posterior por un Driver)</option>
          </select>
        </label>
        <Input label="Dirección (opcional)" value={address} onChange={(e: any) => setAddress(e.target.value)} placeholder="Araoz 123, CABA" />
        <Input label="Código postal (opcional)" value={postalCode} onChange={(e: any) => setPostalCode(e.target.value)} placeholder="1414" />
        {type === "DROPOFF" && financeAccess ? (
          <label style={{ display: "block" }}>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "6px" }}>Tarifa de recepción</div>
            <select value={tariffId} onChange={(e) => setTariffId(e.target.value)} style={selectStyle} disabled={!catalog}>
              <option value="">Sin tarifa asignada</option>
              {(catalog ?? []).map((c) => <option key={c.id} value={c.id}>{c.name} — ${Number(c.amount).toFixed(2)}</option>)}
            </select>
            <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
              Se administra el catálogo completo desde Sistema → Listas de precios → Dropoff. Sin asignar: la recepción sigue, sin valorizar.
            </div>
          </label>
        ) : null}
        {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error}</div> : null}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || !name.trim()}>{saving ? "Guardando…" : "Guardar"}</Button>
        </div>
      </div>
    </Modal>
  );
}

// Geolocalización de Warehouses (ver informe "LYNX — Multi-Warehouse,
// Dropoffs, Pickers y Estanterías", PR 6): mismo buscador y mismo pin en
// mapa que ya usa Stores.tsx (StoreGeoLocationModal) -- useLeaflet,
// buildGeocodeQuery y GET /v2/deliveries/geo/search tal cual están hoy,
// sin tocar ese componente ni su endpoint propio (PUT /v2/stores/:id/
// location sigue siendo exclusivo de Store+Seller). Acá el guardado es
// directo (PUT /v2/warehouses/:id): no hay un segundo actor cuya propuesta
// un Admin deba revisar, como sí pasa con Store+Seller.
type WarehouseGeoPin = { lat: number; lng: number; source: "auto" | "manual"; level?: "high" | "medium" | "low" };
const WH_GEO_LEVEL_COLOR: Record<string, string> = { high: "var(--success)", medium: "var(--warning, #d29922)", low: "var(--danger)" };
const WH_GEO_MANUAL_COLOR = "var(--primary, #4f8cff)";
const WH_GEO_CENTRO: [number, number] = [-34.62, -58.45];

function WarehouseGeoLocationModal({
  warehouseId, address, postalCode, req, onClose, onSaved,
}: {
  warehouseId: number; address: string | null; postalCode: string | null; req: any;
  onClose: () => void; onSaved: (w: WarehouseRow) => void;
}) {
  const leaflet = useLeaflet();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const [addrInput, setAddrInput] = useState(() => buildGeocodeQuery({ direccion: address, codigoPostal: postalCode }));
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchMsg, setSearchMsg] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<GeocodeCandidate[]>([]);
  const [pin, setPin] = useState<WarehouseGeoPin | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = (window as any).L;
    const map = L.map(containerRef.current, { zoomControl: true }).setView(WH_GEO_CENTRO, 9);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18, attribution: "© OpenStreetMap" }).addTo(map);
    map.on("click", (e: any) => setPin({ lat: e.latlng.lat, lng: e.latlng.lng, source: "manual" }));
    mapRef.current = map;
  }, [leaflet]);

  useEffect(() => {
    const L = (window as any).L;
    const map = mapRef.current;
    if (!L || !map) return;
    if (!pin) { markerRef.current?.remove(); markerRef.current = null; return; }
    const color = pin.source === "manual" ? WH_GEO_MANUAL_COLOR : WH_GEO_LEVEL_COLOR[pin.level ?? "low"];
    const icon = L.divIcon({
      className: "",
      html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:${color};border:2px solid #1a1a1a;transform:rotate(-45deg);box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
      iconSize: [22, 22], iconAnchor: [11, 22],
    });
    if (!markerRef.current) {
      const marker = L.marker([pin.lat, pin.lng], { icon, draggable: true }).addTo(map);
      marker.on("dragend", () => { const ll = marker.getLatLng(); setPin({ lat: ll.lat, lng: ll.lng, source: "manual" }); });
      markerRef.current = marker;
    } else {
      markerRef.current.setLatLng([pin.lat, pin.lng]);
      markerRef.current.setIcon(icon);
    }
  }, [pin, leaflet]);

  const buscar = useCallback(async () => {
    setSearchBusy(true); setSearchMsg(null); setCandidates([]);
    try {
      const params = new URLSearchParams();
      if (addrInput.trim()) params.set("direccion", addrInput.trim());
      const j = await req(`/v2/deliveries/geo/search?${params.toString()}`);
      const found: GeocodeCandidate[] = j.candidates ?? [];
      setCandidates(found);
      const best = found[0] ?? null;
      const map = mapRef.current;
      if (best) {
        setPin({ lat: best.latitude, lng: best.longitude, source: "auto", level: best.confidence.level });
        if (map) map.setView([best.latitude, best.longitude], 16);
      } else {
        setPin(null);
        setSearchMsg("Sin resultados para esta búsqueda.");
      }
    } catch (e: any) { setSearchMsg(e.message ?? "Error buscando la dirección"); } finally { setSearchBusy(false); }
  }, [addrInput, req]);

  useEffect(() => { if (leaflet) buscar(); }, [leaflet]); // eslint-disable-line react-hooks/exhaustive-deps

  const elegirCandidato = (c: GeocodeCandidate) => {
    setPin({ lat: c.latitude, lng: c.longitude, source: "auto", level: c.confidence.level });
    const map = mapRef.current;
    if (map) map.setView([c.latitude, c.longitude], 16);
  };

  const confirmar = async () => {
    if (!pin) return;
    setSaving(true); setError(null);
    try {
      const r = await req(`/v2/warehouses/${warehouseId}`, { method: "PUT", body: { latitude: pin.lat, longitude: pin.lng } });
      onSaved(r.warehouse);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal title="Geolocalizar Warehouse" onClose={onClose} width="760px">
      <div style={{ display: "flex", gap: "10px", marginBottom: "12px", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: 1, minWidth: "220px" }}>
          <Input label="Buscar dirección" value={addrInput} onChange={(e: any) => setAddrInput(e.target.value)} placeholder="Araoz 123, 1414, CABA" />
        </div>
        <Button onClick={buscar} disabled={searchBusy}>{searchBusy ? "Buscando..." : "Buscar"}</Button>
      </div>
      {searchMsg ? <div style={{ marginBottom: "10px", color: "var(--text-secondary)", fontSize: "13px" }}>{searchMsg}</div> : null}
      <div ref={containerRef} style={{ width: "100%", height: "320px", borderRadius: "14px", overflow: "hidden", border: "1px solid var(--border)" }} />
      {candidates.length > 1 ? (
        <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "6px" }}>
          {candidates.map((c, i) => (
            <div key={i} onClick={() => elegirCandidato(c)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "10px", cursor: "pointer", border: "1px solid var(--border)" }}>
              <span style={{ display: "inline-block", width: "9px", height: "9px", borderRadius: "50%", background: WH_GEO_LEVEL_COLOR[c.confidence.level] }} />
              <span style={{ fontSize: "13px", color: "var(--text-primary)" }}>{c.displayName ?? c.address ?? `${c.latitude}, ${c.longitude}`}</span>
            </div>
          ))}
        </div>
      ) : null}
      {pin ? <div style={{ ...smallMutedStyle, marginTop: "10px" }}>Pin: {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)} {pin.source === "manual" ? "(ubicado a mano)" : ""}</div> : null}
      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)", marginTop: "10px" }}>{error}</div> : null}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={confirmar} disabled={saving || !pin}>{saving ? "Guardando…" : "Confirmar ubicación"}</Button>
      </div>
    </Modal>
  );
}
